/*
  Our materials (round 37): what moves on sections/vr-materials.liquid.
  - gold: as the ingots come into view their moulds fill with molten gold one after another, each cools to its own
    colour, and then one band of light crosses all four;
  - diamonds: the jeweller's lamp comes on over the velvet, and each diamond paper unfolds as it comes into view;
  - gemstones: the light on the paper takes the colour of the stone being looked at, under the pointer or at the
    middle of the screen as the page is read, and drifts to sit behind it;
  - pearls: a thread of gold strings the pearls together as the page is read.
  Visitors who ask for reduced motion see every chapter finished and still.
*/
(function () {
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasIO = 'IntersectionObserver' in window;
  var ease = function (t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; };
  var once = function (el, threshold, fn, margin) {
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) { if (e.isIntersecting) { io.disconnect(); fn(); } });
    }, { threshold: threshold, rootMargin: margin || '0px' });
    io.observe(el);
  };
  var below = function (el) { return el.getBoundingClientRect().top > window.innerHeight * 0.85; };

  /* ---- gold: the pour ---- */
  function gold(root) {
    var list = root.querySelector('.vrm-ingots');
    if (!list) return;
    var ingots = Array.prototype.slice.call(list.querySelectorAll('[data-vrm-ingot]'));
    ingots.forEach(function (ing, i) { ing.style.setProperty('--b', i); });
    if (reduce || !hasIO || !below(list)) return;
    var TOP = 36, BOTTOM = 136, H = 176;
    var set = function (ing, y) {
      var lv = ing.querySelector('[data-vrm-level]'), ed = ing.querySelector('[data-vrm-edge]');
      if (lv) { lv.setAttribute('y', y.toFixed(2)); lv.setAttribute('height', Math.max(0, H - y).toFixed(2)); }
      if (ed) ed.setAttribute('y', (y - 2.5).toFixed(2));
    };
    ingots.forEach(function (ing) { set(ing, BOTTOM); });
    var pour = function (ing, done) {
      ing.classList.add('is-pouring');
      var t0 = 0, D = 1900;
      var step = function (now) {
        if (!t0) t0 = now;
        var p = Math.min(1, (now - t0) / D);
        set(ing, BOTTOM - (BOTTOM - TOP) * ease(p));
        if (p < 1) window.requestAnimationFrame(step);
        else { ing.classList.remove('is-pouring'); ing.classList.add('is-cast'); if (done) done(); }
      };
      window.requestAnimationFrame(step);
    };
    once(list, 0.35, function () {
      ingots.forEach(function (ing, i) {
        window.setTimeout(function () {
          pour(ing, i === ingots.length - 1 ? function () { window.setTimeout(function () { list.classList.add('is-lit'); }, 1500); } : null);
        }, 300 + i * 800);
      });
    });
  }

  /* ---- diamonds: the lamp, and the papers ---- */
  function diamonds(root) {
    var sec = root.querySelector('[data-vrm-diamonds]');
    if (!sec || reduce || !hasIO) return;
    if (below(sec)) { sec.classList.add('is-armed'); once(sec, 0.12, function () { sec.classList.add('is-lit'); }); }
    Array.prototype.slice.call(sec.querySelectorAll('[data-vrm-paper]')).forEach(function (p, i) {
      if (!below(p)) return;
      p.classList.add('is-armed');
      once(p, 0.55, function () { window.setTimeout(function () { p.classList.add('is-open'); }, 120 + (i % 4) * 170); });
    });
  }

  /* ---- gemstones: the light takes the colour of the stone looked at ---- */
  function gems(root) {
    var sec = root.querySelector('[data-vrm-gems]');
    if (!sec) return;
    var list = Array.prototype.slice.call(sec.querySelectorAll('[data-vrm-gem]'));
    if (!list.length) return;
    var active = null, hovered = null, seen = false, ticking = false;
    var activate = function (g) {
      if (!g || g === active) return;
      if (active) active.classList.remove('is-active');
      active = g;
      g.classList.add('is-active');
      var sr = sec.getBoundingClientRect(), gr = (g.querySelector('.vrm-gem__photo') || g).getBoundingClientRect();
      sec.style.setProperty('--vrm-gem', g.getAttribute('data-glow'));
      sec.style.setProperty('--vrm-gx', (((gr.left + gr.width / 2) - sr.left) / sr.width * 100).toFixed(2) + '%');
      sec.style.setProperty('--vrm-gy', (((gr.top + gr.height / 2) - sr.top) / sr.height * 100).toFixed(2) + '%');
    };
    var byReading = function () {
      ticking = false;
      if (!seen || hovered) return;
      var mid = window.innerHeight * 0.5, best = null, bd = Infinity;
      list.forEach(function (g) {
        var r = g.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) return;
        var d = Math.abs(r.top + r.height * 0.4 - mid) + Math.abs(r.left + r.width / 2 - window.innerWidth / 2) * 0.15;
        if (d < bd) { bd = d; best = g; }
      });
      activate(best);
    };
    var onScroll = function () { if (!ticking) { ticking = true; window.requestAnimationFrame(byReading); } };
    if (hasIO) new IntersectionObserver(function (es) { es.forEach(function (e) { seen = e.isIntersecting; if (seen) onScroll(); }); }, { threshold: 0 }).observe(sec);
    else seen = true;
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    list.forEach(function (g) {
      g.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') { hovered = g; activate(g); } });
      g.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse' && hovered === g) { hovered = null; } });
      g.addEventListener('focus', function () { activate(g); });
    });
    onScroll();
  }

  /* ---- pearls: the thread ---- */
  function pearls(root) {
    var strand = root.querySelector('[data-vrm-strand]');
    if (!strand) return;
    var svg = strand.querySelector('svg'), path = strand.querySelector('[data-vrm-thread]');
    var beads = Array.prototype.slice.call(strand.querySelectorAll('[data-vrm-bead]'));
    if (!svg || !path || !beads.length) return;
    var len = 0, ticking = false;
    var build = function () {
      var sr = strand.getBoundingClientRect(), w = sr.width, h = sr.height;
      if (!w || !h) return;
      svg.setAttribute('viewBox', '0 0 ' + w.toFixed(1) + ' ' + h.toFixed(1));
      var pts = [[w / 2, 0]];
      beads.forEach(function (b) { var r = b.getBoundingClientRect(); pts.push([r.left + r.width / 2 - sr.left, r.top + r.height / 2 - sr.top]); });
      pts.push([w / 2, h]);
      var d = 'M' + pts[0][0].toFixed(1) + ' ' + pts[0][1].toFixed(1);
      for (var i = 1; i < pts.length; i++) {
        var a = pts[i - 1], b = pts[i], dy = (b[1] - a[1]) * 0.5;
        d += ' C' + a[0].toFixed(1) + ' ' + (a[1] + dy).toFixed(1) + ' ' + b[0].toFixed(1) + ' ' + (b[1] - dy).toFixed(1) + ' ' + b[0].toFixed(1) + ' ' + b[1].toFixed(1);
      }
      path.setAttribute('d', d);
      len = path.getTotalLength();
      path.style.strokeDasharray = len.toFixed(1);
      draw();
    };
    var draw = function () {
      ticking = false;
      if (!len) return;
      if (reduce) { path.style.strokeDashoffset = '0'; return; }
      var r = strand.getBoundingClientRect();
      var p = Math.max(0, Math.min(1, (window.innerHeight * 0.62 - r.top) / r.height));
      path.style.strokeDashoffset = (len * (1 - p)).toFixed(1);
    };
    var onScroll = function () { if (!ticking) { ticking = true; window.requestAnimationFrame(draw); } };
    var t = 0;
    var rebuild = function () { window.clearTimeout(t); t = window.setTimeout(build, 150); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', rebuild);
    window.addEventListener('load', build);
    if (window.ResizeObserver) { try { new ResizeObserver(rebuild).observe(strand); } catch (e) {} }
    build();
  }

  function init(root) {
    var vrm = root.matches && root.matches('[data-vrm]') ? root : root.querySelector('[data-vrm]');
    if (!vrm || vrm.dataset.vrmReady) return;
    vrm.dataset.vrmReady = '1';
    gold(vrm);
    diamonds(vrm);
    gems(vrm);
    pearls(vrm);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
